<template>
    <div class="shift-widget" :class="{ 'shift-active': isActive }">
        <div class="shift-header">
            <feather-icon icon="ClipboardIcon" size="18" />
            <span>Vardiya İşlemleri</span>
        </div>

        <div class="shift-body">
            <div class="shift-status">
                <feather-icon :icon="isActive ? 'ActivityIcon' : 'MoonIcon'" size="20" />
                <span>{{ statusText }}</span>
            </div>

            <!-- Pompa yoksa eski tek fotoğraf modu -->
            <template v-if="pumps.length === 0">
                <div class="shift-upload-wrapper">
                    <div v-if="file" class="file-preview-area">
                        <img :src="previewUrl" alt="Önizleme" class="preview-thumb" />
                        <span class="file-name">{{ truncatedFileName }}</span>
                        <button type="button" class="btn-remove" @click="removeFile">
                            <feather-icon icon="XIcon" size="16" />
                        </button>
                    </div>
                    <div v-else class="upload-buttons">
                        <button type="button" class="upload-btn" @click="openCamera">
                            <feather-icon icon="CameraIcon" size="20" />
                            <span>Kamera</span>
                        </button>
                        <button type="button" class="upload-btn" @click="openFilePicker">
                            <feather-icon icon="ImageIcon" size="20" />
                            <span>Fotoğraf Seç</span>
                        </button>
                    </div>
                    <input ref="cameraInput" type="file" accept="image/jpeg,image/png,image/jpg" capture="environment" class="d-none" @change="handleFileSelect" />
                    <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/jpg" class="d-none" @change="handleFileSelect" />
                </div>
            </template>

            <!-- Pompa varsa depo+pompa bazlı fotoğraf modu -->
            <template v-else>
                <div class="pump-photos-container">
                    <div v-for="storage in storageGroups" :key="storage.id" class="storage-group">
                        <div class="storage-group-header">
                            <feather-icon icon="DatabaseIcon" size="14" />
                            <span>{{ storage.name }}</span>
                        </div>
                        <div v-for="pump in storage.pumps" :key="pump.Id" class="pump-row">
                            <div class="pump-label">
                                <strong>{{ pump.Name }}</strong>
                                <span class="badge-meter">{{ pump.MeterType }}</span>
                            </div>
                            <div class="pump-files-area">
                                <div v-for="(pf, idx) in getPumpFiles(pump.Id)" :key="idx" class="pump-file-thumb">
                                    <img :src="pf.previewUrl" alt="" />
                                    <button type="button" class="thumb-remove" @click="removePumpFile(pump.Id, idx)">
                                        <feather-icon icon="XIcon" size="10" />
                                    </button>
                                </div>
                                <button type="button" class="pump-add-btn" title="Kamera" @click="openPumpCamera(pump.Id)">
                                    <feather-icon icon="CameraIcon" size="16" />
                                </button>
                                <button type="button" class="pump-add-btn" title="Dosya Seç" @click="openPumpFilePicker(pump.Id)">
                                    <feather-icon icon="ImageIcon" size="16" />
                                </button>
                            </div>
                            <input :ref="'pumpCamera_' + pump.Id" type="file" accept="image/jpeg,image/png,image/jpg" capture="environment" class="d-none" @change="handlePumpFileSelect($event, pump.Id)" />
                            <input :ref="'pumpInput_' + pump.Id" type="file" accept="image/jpeg,image/png,image/jpg" multiple class="d-none" @change="handlePumpFileSelect($event, pump.Id)" />
                        </div>
                    </div>
                </div>
            </template>

            <b-button class="shift-action-btn" :variant="isActive ? 'warning' : 'primary'" :disabled="loading || processing || !canSubmit" @click="handleAction">
                <b-spinner v-if="loading || processing" small />
                <template v-else>
                    <feather-icon :icon="isActive ? 'StopCircleIcon' : 'PlayCircleIcon'" size="20" />
                    <span>{{ isActive ? "Vardiyayı Bitir" : "Vardiyayı Başlat" }}</span>
                </template>
            </b-button>
        </div>
    </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import FuelStorageApi from "@/api/fuel-storage";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
    name: "ShiftManagementWidget",
    components: { BButton, BSpinner },
    data() {
        return {
            loading: true,
            processing: false,
            isActive: false,
            shiftStart: null,
            file: null,
            previewUrl: null,
            pumps: [],
            pumpFiles: {},
        };
    },
    computed: {
        statusText() {
            if (this.loading) return "Kontrol ediliyor...";
            if (!this.isActive) return "Vardiya Kapalı";
            return this.shiftStart ? `${this.shiftStart}'dan beri aktif` : "Vardiya Aktif";
        },
        truncatedFileName() {
            if (!this.file) return "";
            const name = this.file.name;
            return name.length > 25 ? name.substring(0, 22) + "..." : name;
        },
        canSubmit() {
            if (this.pumps.length === 0) return !!this.file;
            return Object.values(this.pumpFiles).some(files => files && files.length > 0);
        },
        storageGroups() {
            const groups = {};
            this.pumps.forEach(pump => {
                const key = pump.FuelStorageId;
                if (!groups[key]) {
                    groups[key] = { id: key, name: pump.FuelStorageName || "Depo", pumps: [] };
                }
                groups[key].pumps.push(pump);
            });
            return Object.values(groups);
        },
    },
    mounted() {
        this.checkStatus();
        this.loadPumps();
    },
    beforeDestroy() {
        this.revokePreview();
        this.revokeAllPumpPreviews();
    },
    methods: {
        async loadPumps() {
            try {
                const res = await FuelStorageApi.FuelStoragePump.GetByCurrentUser();
                if (res.data.Success) {
                    this.pumps = res.data.Data || [];
                }
            } catch (error) {
                console.error("Pompalar yüklenemedi", error);
            }
        },
        checkStatus() {
            this.loading = true;
            FuelStorageApi.FuelStorageShift.GetActiveStatus()
                .then((res) => {
                    const data = res.data.Data;
                    if (data && data.Status) {
                        this.isActive = true;
                        const match = data.Message.match(/(\d{2}\.\d{2}\.\d{4} \d{2}:\d{2})/);
                        this.shiftStart = match ? match[1] : null;
                    } else {
                        this.isActive = false;
                        this.shiftStart = null;
                    }
                })
                .catch(() => {})
                .finally(() => { this.loading = false; });
        },
        handleAction() {
            this.processing = true;
            const formData = new FormData();

            if (this.pumps.length === 0) {
                if (this.file) formData.append("File", this.file);
            } else {
                const pumpIds = [];
                Object.keys(this.pumpFiles).forEach(pumpId => {
                    const files = this.pumpFiles[pumpId];
                    if (files) {
                        files.forEach(pf => {
                            formData.append("Files", pf.file);
                            pumpIds.push(pumpId);
                        });
                    }
                });
                formData.append("PumpIds", JSON.stringify(pumpIds));
            }

            const apiCall = this.isActive
                ? FuelStorageApi.FuelStorageShift.End(formData)
                : FuelStorageApi.FuelStorageShift.Start(formData);

            apiCall
                .then((res) => {
                    const isSuccess = res.data.Status === true || res.data.Success === true;
                    if (isSuccess) {
                        this.showToast("Başarılı", res.data.Message, "success", "CheckCircleIcon");
                        this.removeFile();
                        this.clearAllPumpFiles();
                        this.checkStatus();
                    } else {
                        this.showToast("Başarısız", res.data.Message, "danger", "XCircleIcon");
                    }
                })
                .catch(() => {
                    this.showToast("Hata", "İşlem sırasında bir hata oluştu.", "danger", "AlertTriangleIcon");
                })
                .finally(() => { this.processing = false; });
        },
        // Tek fotoğraf
        openCamera() { this.$refs.cameraInput.click(); },
        openFilePicker() { this.$refs.fileInput.click(); },
        handleFileSelect(e) {
            const f = e.target.files[0];
            if (f) this.setFile(f);
        },
        setFile(newFile) {
            this.revokePreview();
            this.file = newFile;
            this.previewUrl = URL.createObjectURL(newFile);
        },
        removeFile() {
            this.revokePreview();
            this.file = null;
            this.previewUrl = null;
            if (this.$refs.cameraInput) this.$refs.cameraInput.value = "";
            if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        },
        revokePreview() { if (this.previewUrl) URL.revokeObjectURL(this.previewUrl); },
        // Pompa bazlı
        getPumpFiles(pumpId) { return this.pumpFiles[pumpId] || []; },
        openPumpCamera(pumpId) {
            const refs = this.$refs['pumpCamera_' + pumpId];
            if (refs && refs[0]) refs[0].click();
        },
        openPumpFilePicker(pumpId) {
            const refs = this.$refs['pumpInput_' + pumpId];
            if (refs && refs[0]) refs[0].click();
        },
        handlePumpFileSelect(e, pumpId) {
            const files = Array.from(e.target.files);
            if (!this.pumpFiles[pumpId]) this.$set(this.pumpFiles, pumpId, []);
            files.forEach(f => {
                this.pumpFiles[pumpId].push({ file: f, previewUrl: URL.createObjectURL(f) });
            });
            e.target.value = "";
        },
        removePumpFile(pumpId, idx) {
            const pf = this.pumpFiles[pumpId][idx];
            if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
            this.pumpFiles[pumpId].splice(idx, 1);
        },
        clearAllPumpFiles() {
            this.revokeAllPumpPreviews();
            this.pumpFiles = {};
        },
        revokeAllPumpPreviews() {
            Object.values(this.pumpFiles).forEach(files => {
                if (files) files.forEach(pf => { if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl); });
            });
        },
        showToast(title, text, variant, icon) {
            this.$toast({ component: ToastificationContent, props: { title, text, variant, icon } });
        },
    },
};
</script>

<style scoped>
.shift-widget { background: #fff; border: 1px solid #ebe9f1; border-radius: 10px; box-shadow: 0 2px 6px rgba(0,0,0,0.04); overflow: hidden; }
.shift-widget.shift-active { border-color: rgba(255,159,67,0.5); }
.shift-header { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1.25rem; background: #f8f8f8; border-bottom: 1px solid #ebe9f1; font-size: 0.95rem; font-weight: 600; color: #5e5873; }
.shift-active .shift-header { background: rgba(255,159,67,0.08); border-bottom-color: rgba(255,159,67,0.2); }
.shift-body { display: flex; flex-direction: column; gap: 0.8rem; padding: 0.85rem; }
.shift-status { display: flex; align-items: center; gap: 0.5rem; padding: 0.65rem 1rem; background: rgba(108,117,125,0.1); color: #6c757d; border-radius: 8px; font-size: 0.9rem; font-weight: 600; }
.shift-active .shift-status { background: rgba(255,159,67,0.12); color: #ff9f43; }
.shift-upload-wrapper { display: flex; min-width: 0; }
.upload-buttons { display: flex; gap: 0.75rem; flex: 1; }
.upload-btn { display: flex; align-items: center; justify-content: center; gap: 0.5rem; flex: 1; padding: 0.65rem 1rem; border: 1.5px dashed #d8d6de; border-radius: 8px; background: transparent; color: #6e6b7b; font-size: 0.9rem; font-weight: 500; cursor: pointer; }
.upload-btn:hover { border-color: #0d6efd; color: #0d6efd; }
.file-preview-area { display: flex; align-items: center; gap: 0.75rem; flex: 1; padding: 0.5rem 0.75rem; border: 1.5px solid #28c76f; border-radius: 8px; background: rgba(40,199,111,0.04); }
.preview-thumb { width: 44px; height: 44px; border-radius: 6px; object-fit: cover; }
.file-name { flex: 1; font-size: 0.88rem; color: #28c76f; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.btn-remove { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border: none; border-radius: 6px; background: rgba(234,84,85,0.1); color: #ea5455; cursor: pointer; }
.shift-action-btn { display: flex; align-items: center; justify-content: center; gap: 0.5rem; padding: 0.7rem 1.25rem; font-size: 0.95rem; font-weight: 600; width: 100%; }

/* Depo+Pompa bazlı fotoğraf */
.pump-photos-container { display: flex; flex-direction: column; gap: 0.75rem; width: 100%; }
.storage-group { border: 1px solid #ebe9f1; border-radius: 8px; overflow: hidden; }
.storage-group-header { display: flex; align-items: center; gap: 0.4rem; padding: 0.5rem 0.75rem; background: #7367f0; color: #fff; font-size: 0.82rem; font-weight: 600; }
.pump-row { display: flex; align-items: center; gap: 0.75rem; padding: 0.6rem 0.75rem; border-bottom: 1px solid #f3f2f7; }
.pump-row:last-child { border-bottom: none; }
.pump-label { min-width: 130px; flex-shrink: 0; }
.pump-label strong { font-size: 0.85rem; display: block; }
.badge-meter { display: inline-block; font-size: 0.7rem; padding: 0.1rem 0.4rem; border-radius: 3px; background: #f0f0f0; color: #6e6b7b; }
.pump-files-area { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; flex: 1; }
.pump-file-thumb { position: relative; width: 44px; height: 44px; }
.pump-file-thumb img { width: 100%; height: 100%; object-fit: cover; border-radius: 4px; border: 1px solid #ebe9f1; }
.thumb-remove { position: absolute; top: -5px; right: -5px; width: 18px; height: 18px; border: none; border-radius: 50%; background: #ea5455; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
.pump-add-btn { width: 44px; height: 44px; border: 1.5px dashed #d8d6de; border-radius: 4px; background: transparent; color: #6e6b7b; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.pump-add-btn:hover { border-color: #7367f0; color: #7367f0; }

/* Dark Mode */
.dark-layout .shift-widget { background: #283046; border-color: #3b4253; }
.dark-layout .shift-header { background: #242b3d; border-bottom-color: #3b4253; color: #b4b7bd; }
.dark-layout .storage-group { border-color: #3b4253; }
.dark-layout .pump-row { border-bottom-color: #3b4253; }
.dark-layout .badge-meter { background: #3b4253; color: #b4b7bd; }

@media (max-width: 767.98px) {
    .pump-label { min-width: 90px; }
    .pump-row { flex-wrap: wrap; }
}
</style>
